import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import classNames from "classnames";
import StepHeader from "../components/StepHeader";
import VehicleCard from "../components/VehicleCard";
import request from "../../../network/request";
import ReservationItems from "../components/ReservationItems";
import RemarkContent from "../components/RemarkContent";
import StoreAndTimeSelector from "../pickupTimeAndShop/indexV2";
import ContactForm from "../ContactForm";
import useBasicFormData from "../store/useBasicFormData";
import useBaseInfo from "../store/useBaseInfo";
import useGetCh from "../../../hooks/useGetCh";
import Loading from "../../../components/CommonLoading";
import { reportUserBehavior } from "../../../utils/report";
import usePickupAddress from "../store/usePickupAddress";
import useSelfArrivalCity from "../store/useSelfArrivalCity";
import useQuery from "../../../hooks/useQuery";
import { RESERVE_STEP_INDEX, MAINTANCE_TYPE_CODE } from "../config/constants";
import ConfirmModifyModal from "../components/ConfirmModifyModal";
import LoadingHomepage from "../components/LoadingHomepage";
import { paddingLeftZero } from "../../../utils/date";
import styles from "./index.module.scss";
import moment from "moment";
import useUserRightRequest from "../store/useUserRightRequest";
import { RESULT_CODE } from "../../../constants";
import SubmitErrorAlert, {
  SubmitErrorAlertRef,
} from "../components/SubmitErrorAlert";
import { findServiceItemsParam } from "../utils";
import useReserveStepDocumentTitle from "../hooks/useReserveStepDocumentTitle";
import {
  ReservationItemTypes,
  RESERVATION_DISPLAY_TYPE,
  ReserveOrderTypes,
} from "../interfaces/home";
import useGlobalInitData from "../store/useGlobalInitData";
import CityPicker from "../CityPicker";
import useReserveStoreInfo from "../hooks/useReserveStoreInfo";
import useReserveTimeInfo from "../hooks/useReserveTimeInfo";
import useReserveStep from "../hooks/useReserveStep";
import { useMount } from "ahooks";

export default function EditingPage() {
  const ch = useGetCh();
  const history = useHistory();
  const { reserveId: reserveOrderCode } = useParams<any>();
  const query = useQuery();
  const reserveId = query.get("reserveId");
  const { globalInitData } = useGlobalInitData();
  const {
    baseInfo,
    setVehicle,
    setModifyReserveId,
    addPlateNumber,
    setEditingMode,
  } = useBaseInfo();
  const {
    basicFormData,
    setContactInfoData,
    setModifyOrderArriveStoreTime,
    setModifyOrderShopCode,
    resetStoreAndDateData,
  } = useBasicFormData();
  const { pickupAddress, setPickupAddress } = usePickupAddress();
  const [modifyingOrderData, setModifyOrderData] =
    useState<ReserveOrderTypes>();
  const { setSelfArrivalCity } = useSelfArrivalCity();
  const [isLoading, setIsLoading] = useState(true);
  const setDefaultOrderData = useRef(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const submitLockRef = useRef(false);
  const loadedModifyDataRef = useRef(false);
  const { runAsync: getUserRight } = useUserRightRequest();
  const submitErrorAlertRef = useRef<SubmitErrorAlertRef>(null);

  const [reserveItems, setReserveItems] = useState<ReservationItemTypes[]>([]);
  const [remarkContent, setRemarkContent] = useState("");
  const {
    currentStep: currentFormStep,
    canGoNextStep,
    prev,
    next,
  } = useReserveStep({ reserveItems });

  const reserveStoreInfo = useReserveStoreInfo();
  const reserveTimeInfo = useReserveTimeInfo();

  const btnDisabled = !canGoNextStep;

  useReserveStepDocumentTitle(currentFormStep);

  useEffect(() => {
    setEditingMode(true);
    return () => {
      console.info(" Editing page unmountted......");
      setEditingMode(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setModifyReserveId(reserveId);
  }, [reserveId, setModifyReserveId]);

  const setNextStep = () => {
    next(async (_, current) => {
      if (current === RESERVE_STEP_INDEX.TIME_STORE) {
        try {
          // 获取取送车权益信息
          Loading.show();
          await getUserRight({
            vin: baseInfo?.frameNumber,
            reserveId,
            preComeDate: reserveTimeInfo
              ? `${reserveTimeInfo.date} ${reserveTimeInfo.startTime}`
              : "",
          });
        } finally {
          Loading.hide();
        }
      }
    });
  };

  const setPreStep = () => prev();

  // 设置默认选中的预约项目内容
  const setSelectedServiceItems = ({
    reserveTypes,
    repairDescList,
    specialActivityCode,
  }) => {
    const { serviceItemState } = basicFormData.reserveItemsStepData;
    const reserveTypesList = reserveTypes ? reserveTypes.split(",") : [];
    // 可预约类型 = 后端返回的可预约类型 + 当前预约类型 + 如果当前是专项预约时，可预约专项+常规项目
    const canReserveType = [
      ...globalInitData.reserveGoingRecordDto.canReserveType,
    ];
    if (specialActivityCode) {
      canReserveType.push(
        RESERVATION_DISPLAY_TYPE.SPECIAL,
        RESERVATION_DISPLAY_TYPE.NORMAL
      );
    } else {
      canReserveType.push(RESERVATION_DISPLAY_TYPE.NORMAL);
    }

    if (reserveTypesList.length === 0) return;
    const updatedList = serviceItemState
      .filter((item) => canReserveType.includes(item.displayType))
      .map((item) => {
        if (item.displayType === RESERVATION_DISPLAY_TYPE.SPECIAL) {
          return {
            ...item,
            checked: item.eventCode === specialActivityCode,
          };
        }

        const { code } = item;
        if (reserveTypesList.indexOf(`${code}`) >= 0) {
          // 维修选项有选中
          if (
            code === MAINTANCE_TYPE_CODE.REPAIR &&
            repairDescList &&
            repairDescList.length > 0
          ) {
            return {
              ...item,
              checked: true,
              children: item.children.map((subItem) => {
                return {
                  ...subItem,
                  checked: repairDescList.indexOf(subItem.code) >= 0,
                };
              }),
            };
          }
          return {
            ...item,
            checked: true,
          };
        } else {
          return {
            ...item,
            checked: false,
          };
        }
      });
    setReserveItems(updatedList);
  };

  const fetchReserveDetailData = useCallback(async () => {
    try {
      const res: any = await request(
        "serviceOrder/getReserveDetail",
        {
          reserveOrderCode: reserveOrderCode,
        },
        "post"
      );
      setModifyOrderData(res);
    } finally {
      setIsLoading(false);
      loadedModifyDataRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 根据目标修改订单信息，重置表单各项初始化数据
  useEffect(() => {
    if (
      !modifyingOrderData ||
      !basicFormData.reserveItemsStepData ||
      setDefaultOrderData.current
    ) {
      return;
    }
    console.log(
      "开始修改预约单，并设置预约单的默认信息:::::::",
      modifyingOrderData
    );
    const {
      reserveTel,
      reserveMan,
      vin,
      plateNumber,
      csDesc,
      orderType,
      specialActivityCode, // 专项活动
      descList,
      shopAddress,
      // serviceTime,
      preComeDate,
      bookingDate,
      drivingDto,
    } = modifyingOrderData;
    // 步骤 1 数据
    setVehicle(vin, plateNumber);
    setRemarkContent(csDesc);
    setSelectedServiceItems({
      reserveTypes: orderType || "",
      repairDescList: descList || [],
      specialActivityCode: specialActivityCode || "",
    });
    setModifyOrderArriveStoreTime(preComeDate);

    const usePickup = !!drivingDto;

    // 步骤 2 数据
    if (shopAddress) {
      const { cityCode, cityName, code: shopCode } = shopAddress;
      const pCode = Math.floor(Number(cityCode) / 100) + "";
      // 设置当前选中城市
      setSelfArrivalCity({
        cityCode,
        cityName,
        provinceCode: pCode,
        address: "",
      });
      setModifyOrderShopCode(shopCode);
    }

    const pickupDateHours = moment(bookingDate).hours();
    const pickupDateminutes = moment(bookingDate).minutes();
    const hmStr = `${paddingLeftZero(pickupDateHours)}:${paddingLeftZero(
      pickupDateminutes
    )}`;

    // 步骤 3 数据
    setContactInfoData({
      // loadedServiceRight: false, // 重置权益接口
      contactName: reserveMan,
      contactPhone: reserveTel,
      usePickupService: usePickup,
      pickupServiceTime: usePickup ? hmStr : "",
    });

    if (usePickup) {
      // 取送车信息
      console.log("设置取送车信息中:", drivingDto?.pickupAddress || "");

      setPickupAddress({
        drivingPickupAddress: drivingDto?.pickupAddress || "",
        drivingPickupAddressLat: `${drivingDto?.pickupLat}` || "",
        drivingPickupAddressLng: `${drivingDto?.pickupLng}` || "",
        drivingPickupCityCode: shopAddress?.cityCode || "",
        drivingPickupCityName: shopAddress?.cityName || "",
      });
    }
    // 原有的创建单，可能是手动添加
    addPlateNumber(plateNumber);
    setDefaultOrderData.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifyingOrderData, basicFormData.reserveItemsStepData]);

  const onModifyModalConfirm = () => {
    submitReservationHandler();
    setShowConfirmModal(false);
  };

  /**
   * 修改预约单
   */
  const submitReservationHandler = async () => {
    if (btnDisabled || submitLockRef.current) return;
    submitLockRef.current = true;

    let reqData: any = {};

    const { usePickupService, contactName, contactPhone, pickupServiceTime } =
      basicFormData.contactInfoStepData;

    const { serviceItems, repairDescList, eventCode, capacityType } =
      findServiceItemsParam(reserveItems);

    reqData = {
      reserveId: reserveId, // 修改单须传
      isDriving: usePickupService ? 1 : 0,
      serviceItems, // 常规服务项目编码
      eventCode, // 专项服务项目编码
      vin: baseInfo?.frameNumber,
      plateNumber: baseInfo?.plateNumber || baseInfo?.addedPlateNumber,
      reserveTel: contactPhone,
      reserveMan: contactName,
      csDesc: remarkContent,
      capacityType,
      descList: repairDescList, // 勾选的维修描述
      shopUuid: reserveStoreInfo?.uuid,
      shopCode: reserveStoreInfo?.code,
      cityCode: reserveStoreInfo?.cityCode,
      cityName: reserveStoreInfo?.city,
      preComeDate: `${reserveTimeInfo?.date} ${reserveTimeInfo?.startTime}`,
    };

    // 是否使用取送车服务
    if (usePickupService) {
      reqData.drivingPickupAddressLng = pickupAddress?.drivingPickupAddressLng;
      reqData.drivingPickupAddressLat = pickupAddress?.drivingPickupAddressLat;
      reqData.drivingPickupAddress = pickupAddress?.drivingPickupAddress?.slice(
        0,
        99
      );
      reqData.drivingBookingTime = `${reserveTimeInfo?.date} ${pickupServiceTime}`;
    }

    console.log("before send create reservation Order, paramsssss", reqData);

    reportUserBehavior({
      so: "J001005",
      mcode: "maintence__click_submit_order",
      ch,
      pa: "",
    });

    try {
      Loading.show("提交中");
      const res: any = await request(
        "/public/v2/module/maintenance/update",
        { ...reqData },
        "POST",
        {
          useCustomUrl: true,
          pureReturn: true,
        }
      );
      console.log("修改预约结果", res);
      const { code, data } = res;
      if (code === 200) {
        history.replace(
          `/reservation/success/${data.reserveOrderCode}?ch=${ch}&today=${
            data.today ? "1" : "0"
          }&withinWorkHour=${data.withinWorkHour ? "1" : "0"}`
        );
        reportUserBehavior({
          so: "J001005",
          mcode: "maintence__req_submit_order_success",
          ch,
          pa: "",
        });
      } else {
        console.error("修改预约失败", res);
        if (
          code === RESULT_CODE.GLOBAL_RATE_LIMITING ||
          code === RESULT_CODE.RESERVE_LOCKED
        ) {
          submitErrorAlertRef.current?.open(res.msg || "系统繁忙，请稍后再试");
        } else {
          // 包含特殊项（如专项）
          const isIncludeSpecialType = !!eventCode;
          history.push(
            `/reservation/fail?ch=${ch}&errorcode=${res.code}&failtext=${res.msg}` +
              (isIncludeSpecialType ? "&special=1" : "")
          );
        }
      }
    } catch (err) {
      reportUserBehavior({
        so: "J001005",
        mcode: "maintence__req_submit_order_error",
        ch,
        pa: `msg=${(err as any).message || "提交失败"}`,
      });
      console.error("修改预约失败", (err as any).message);
      history.push(
        `/reservation/fail?ch=${ch}&failtext=${(err as any).message}`
      );
    } finally {
      submitLockRef.current = false;
      Loading.hide();
    }
  };

  const onShowModifyConfirm = () => {
    if (btnDisabled) return;
    setShowConfirmModal(true);
  };

  useMount(() => {
    fetchReserveDetailData();
  });

  if (isLoading) {
    return <LoadingHomepage></LoadingHomepage>;
  }

  return (
    <div className={styles.homepage}>
      <StepHeader current={currentFormStep} />
      {currentFormStep === RESERVE_STEP_INDEX.RESERVE_ITEMS && (
        <React.Fragment>
          <VehicleCard />
          <ReservationItems
            reserveItems={reserveItems}
            onChange={(newReserveItems) => {
              setReserveItems(newReserveItems);
              resetStoreAndDateData();
            }}
          />
          <RemarkContent content={remarkContent} onChange={setRemarkContent} />
        </React.Fragment>
      )}
      {currentFormStep === RESERVE_STEP_INDEX.TIME_STORE && (
        <StoreAndTimeSelector reserveItems={reserveItems} />
      )}
      {currentFormStep === RESERVE_STEP_INDEX.CONTACT_INFO && (
        <ContactForm
          reserveItems={reserveItems}
          remarkContent={remarkContent}
        />
      )}

      <div className={styles.bottomActionField}>
        {currentFormStep !== RESERVE_STEP_INDEX.RESERVE_ITEMS && (
          <div className={styles.preStepBtn} onClick={setPreStep}>
            上一步
          </div>
        )}
        {currentFormStep !== RESERVE_STEP_INDEX.CONTACT_INFO ? (
          <div
            className={classNames(styles.nextStepBtn, {
              [styles.mediumBtn]:
                currentFormStep !== RESERVE_STEP_INDEX.RESERVE_ITEMS,
              [styles.btnDisabled]: btnDisabled,
            })}
            onClick={setNextStep}
          >
            下一步
          </div>
        ) : (
          <div
            className={classNames(`${styles.nextStepBtn} ${styles.mediumBtn}`, {
              [styles.btnDisabled]: btnDisabled,
            })}
            onClick={onShowModifyConfirm}
          >
            提交修改
          </div>
        )}
      </div>
      <ConfirmModifyModal
        showModal={showConfirmModal}
        onConfirm={onModifyModalConfirm}
        onCancel={() => setShowConfirmModal(false)}
      />
      <SubmitErrorAlert ref={submitErrorAlertRef} />
      <CityPicker />
    </div>
  );
}
